'use client'
import { type Dispatch, type SetStateAction, useState, type FC, useMemo } from 'react'
import { useParams, usePathname, useRouter } from 'next/navigation'
import styled from 'styled-components'
import { motion, type Variants } from 'motion/react'
import type { TypeCmsLayoutLayoutItem, TypeLang, TypeCmsLayout } from '@/schema'
import { Link } from '../Link'
import { designGrid, getImgTransition, getP18, getP24_2, mq, vw } from '@/styles'
import { useDeviceType, useModal } from '@/hooks'
import { MODALS } from '@/context'
import { Img } from '../Img'
import { LOCALE, LANGUAGES } from '@/utils'
import { NavigateOptions } from 'next/dist/shared/lib/app-router-context.shared-runtime'
import { ConditionalWrapper } from '@/components'

type Props = {
  data: TypeCmsLayout['navbar']
  levelLinks: TypeCmsLayoutLayoutItem[]
  setLevelLinks: Dispatch<SetStateAction<TypeCmsLayoutLayoutItem[] | null>>
}

const LINKS_HARD_CODED = [
  {
    // products: {
    //   url: '/products/all',
    // },
    // projects: {
    //   url: '/projects/all',
    // },
    // collections: {
    //   // url: '/collection/architectural'
    // },
    // services: {
    //   // url: '/service/light-designer'
    // },
    // about: {
    //   url: '/about/the-company'
    // }
  }
]

const TRANSLATED_KEYS = {
  es: {
    productos: 'products',
    proyectos: 'projects',
    colecciones: 'collections',
    servicios: 'services',
    about: 'about'
  },
  fr: {
    produits: 'products',
    projets: 'projects',
    collections: 'collections',
    services: 'services',
    'à propos': 'about'
  },
  it: {
    prodotti: 'products',
    progetti: 'projects',
    collezioni: 'collections',
    servizi: 'services',
    attorno: 'about'
  }
}

const removeLocaleFromPath = (pathname: string): string => {
  const matchedLocale = LANGUAGES.find(locale => pathname.startsWith(`/${locale}/`) || pathname === `/${locale}`)
  if (matchedLocale) return pathname.replace(`/${matchedLocale}`, '') || '/'
  return pathname
}

const normalize = str => str.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()

const getParams = (params: TypeCmsLayoutLayoutItem['url']['params']):string => {
  if(!params) return ''

  const paramNamesArray = Object.keys(params)
  let str = ''

  paramNamesArray.forEach((param, idx) => {
    str += `${param}=${params[param]?.value}`
    if (idx < paramNamesArray.length - 1) str += '&'
  })

  return str
}

const getHref = (href, lang) => {
  const locale = lang ?? LOCALE

  const isDefaultLocale = locale === LOCALE
  const isRootPath = href === '/'

  if (isRootPath) {
    return isDefaultLocale ? '/' : `/${locale}`
  }

  return `${isDefaultLocale ? '' : `/${locale}`}${href.startsWith('/') ? '' : '/'}${href}`
}

const LinkExternal = ({ href, children, ...props }) => {
  const { lang }: { lang: TypeLang } = useParams()
  const hrefWithLang = getHref(href, lang)
  return (
    <a href={hrefWithLang} {...props}>{children}</a>
  )
}

const getLink = (data: TypeCmsLayoutLayoutItem, index, pathname: string, _setLevelLinks, setMedia, setCaption, _filteredLinks, setFilteredLinks, updatePayload) => {
  const { caption, medium, title, url, links } = data
  const itemParams = getParams(url.params)
  const itemHref = `${url.type === 'products' ? '/products' : ''}${url.page ? `/${url.page}` : ''}${url.slug ? `/${url.slug}` : ''}${itemParams ? `?${itemParams}` : ''}`
  const active = removeLocaleFromPath(pathname) === itemHref
  const onMouseOver = () => {
    setCaption(caption)
    setMedia(medium)
    if(links) setFilteredLinks?.(links)
  }

  const samePath = pathname.includes(`/${url.type}/${url.slug}`) || pathname.includes(`/${url.page}/${url.slug}`)

  const linkProps = {
    ...active && { className: 'ac' },
    onMouseOver,
    onClick: () => { updatePayload({ close: true, open: false }) }
  }

  return (
    <Dd key={`${index}--btn-${title}`} {...index === 0 && { className: 'first' }} variants={VARIANTS_ITEMS}>
      <ConditionalWrapper condition={!samePath}
        wrapperTrue={(children) => <Link href={itemHref} {...linkProps}>{children}</Link>}
        wrapperFalse={(children) => <LinkExternal href={itemHref} {...linkProps}>{children}</LinkExternal>}>
        <span>{title}</span>
      </ConditionalWrapper>
    </Dd>
  )
}

const getButton = (data: TypeCmsLayoutLayoutItem, index, _pathname: string, setLevelLinks, setMedia, setCaption, _filteredLinks, setFilteredLinks, updatePayload, isMobile, push, lang) => {
  const { caption, medium, links, title } = data
  const updateSubLevel = () => {
    setCaption(caption)
    setMedia(medium)
    setLevelLinks(links)
    setFilteredLinks(null)
  }

  const onClick = () => {
    if (!isMobile) {
      const lowerTitle = normalize(title)
      let baseKey = lowerTitle

      if (lang !== 'en') {
        const translatedMap = TRANSLATED_KEYS[lang] || {}
        baseKey = translatedMap[lowerTitle]
      }

      const matchedUrl = baseKey ? LINKS_HARD_CODED[0][baseKey]?.url : null

      if (matchedUrl) {
        const localizedUrl = lang === 'en' ? matchedUrl : `/${lang}${matchedUrl}`
        push(localizedUrl)
        updatePayload({ close: true, open: false })
      }
    }

    updateSubLevel()
  }

  return (
    <Dd key={`${index}--l-${title}`} {...index === 0 && { className: 'first' }} variants={VARIANTS_ITEMS} >
      <button onClick={onClick} onMouseOver={updateSubLevel}>
        <span>{title === 'Culture x Light' || title === 'Cultura x Luce' || title === 'Culture x Lumière'  ? 'Manifesto' : title}</span>
      </button>
    </Dd>
  )
}

const getTitle = (data: TypeCmsLayoutLayoutItem, index, pathname: string, setLevelLinks, setMedia, setCaption, filteredLinks, setFilteredLinks, updatePayload) => {
  if(!data) return <></>
  const { links, title } = data
  if (!title || !links) return <></>

  const arrOfLinks = (index === 1 && filteredLinks?.length > 1) ? filteredLinks.filter(item => item.type === 'simple') : links
  return <DivGroup key={`${index}-sub-${title}`} className='g--sublevel ba' variants={VARIANTS_LIST_GROUP} initial='initial' animate='animate' exit='exit'>
    <motion.dt variants={VARIANTS_ITEMS_TITLE}>{title}</motion.dt>
    {arrOfLinks && getItems(arrOfLinks, pathname, setLevelLinks, setMedia, setCaption, filteredLinks, setFilteredLinks, updatePayload)}
  </DivGroup>
}

const mapByType = {
  simple: getLink,
  button: getButton,
  title: getTitle
}

const getItems = (data: TypeCmsLayoutLayoutItem[], pathname: string, setLevelLinks, setMedia, setCaption, filteredLinks, setFilteredLinks, updatePaylodad, isMobile?: boolean, push?: (href: string, options?: NavigateOptions) => void, lang?: string) => data.map((item, idx) => {
  const fn = mapByType[item.type]
  return fn(item, idx, pathname, setLevelLinks, setMedia, setCaption, filteredLinks, setFilteredLinks, updatePaylodad, isMobile, push, lang)
})

export const Menu: FC<Props> = ({ data, levelLinks, setLevelLinks }) => {
  const { isActive, updatePayload } = useModal(MODALS.MENU)
  const pathname = usePathname()
  const { top_links, middle_links, bottom_links } = data
  const [media, setMedia] = useState<TypeCmsLayoutLayoutItem['medium']>(null)
  const [caption, setCaption] = useState<TypeCmsLayoutLayoutItem['caption']>(null)
  const [filteredLinks, setFilteredLinks] = useState<TypeCmsLayoutLayoutItem[]>([])
  const { lang }: { lang:TypeLang } = useParams()
  const { isMobile } = useDeviceType()
  const { push } = useRouter()

  const TopLinks = useMemo(() => getItems(top_links, pathname, setLevelLinks, setMedia, setCaption, filteredLinks, setFilteredLinks, updatePayload, isMobile, push, lang), [top_links, pathname, setLevelLinks, filteredLinks, updatePayload, isMobile, push, lang])
  const MiddleLinks = useMemo(() => getItems([middle_links[0]], pathname, setLevelLinks, setMedia, setCaption, filteredLinks, setFilteredLinks, updatePayload, isMobile, push, lang), [middle_links, pathname, setLevelLinks, filteredLinks, updatePayload, isMobile, push, lang])
  const BottomLinks = useMemo(() => getItems(bottom_links, pathname, setLevelLinks, setMedia, setCaption, filteredLinks, setFilteredLinks, updatePayload, isMobile, push, lang), [bottom_links, pathname, setLevelLinks, filteredLinks, updatePayload, isMobile, push, lang])

  const SubLevelLinksTitle = useMemo(() => levelLinks ? getItems(levelLinks, pathname, setLevelLinks, setMedia, setCaption, filteredLinks, setFilteredLinks, updatePayload) : [], [levelLinks, pathname, setLevelLinks, setMedia, setCaption, filteredLinks, setFilteredLinks, updatePayload])

  return (
    <Nav>
      <dl>
        {levelLinks && <>
          {levelLinks?.[0]?.type === 'title' ?
            <>{SubLevelLinksTitle}</>
            :
            <DivGroup key={`s-${levelLinks?.[0]?.title}`} className='g--sublevel' variants={VARIANTS_LIST_GROUP} initial='initial' animate='animate' exit='exit'>
              <dt className='srt-only'>Submenu</dt>
              {getItems(levelLinks, pathname, setLevelLinks, setMedia, setCaption, filteredLinks, setFilteredLinks, updatePayload)}
            </DivGroup>
          }
        </>}
        <DivGroup className='g--root' variants={VARIANTS_LIST_GROUP} initial='initial' animate={isActive ? 'animate' : 'exit'} exit='exit'>
          <dt className='srt-only'>Menu links</dt>
          {TopLinks}
          {MiddleLinks}
          {BottomLinks}
          <Dd key={'lang'} className='first' variants={VARIANTS_ITEMS} >
            <ul className='list--langs'>
              {LANGUAGES.map((item, idx) => (
                <li key={idx}>
                  <a href={`/${item}`} className={`${lang === item ? 'active' : ''}`}>
                    {item}
                  </a>
                </li>
              ))}
            </ul>
          </Dd>
        </DivGroup>
        <div className='g--image'>
          <dd>
            <figure>
              {media && <Img key={media.id} className='media' media={media.image} />}
              <figcaption>{caption}</figcaption>
            </figure>
          </dd>
        </div>
      </dl>
    </Nav>
  )
}

const Nav = styled.nav`
  .btn--back {
    background: var(--color-background);
    height: 50px;
    position: fixed;
    top: 0;
    width: 50px;
    z-index: 2;
  }

  dl {
    ${designGrid({})}
    padding-bottom: 60px;
    position: relative;
  }

  dt {
    ${mq.greaterThan('tablet')} {
      margin-bottom: ${vw(30, 'desktop')};
    }

    ${mq.greaterThan('desktop')} {
      margin-bottom: 30px;
    }

    + dd.first {
      margin-top: 0;
    }
  }

  div {
    &.g--image {
      display: none;

      ${mq.greaterThan('tablet')} {
        align-items: flex-start;
        display: flex;
        grid-column: 10 / span 3;
        grid-row: 1 / span 1;
        justify-content: flex-end;
      }

      figure {
        width: ${vw(320, 'desktop')};
      }

      .media {
        ${getImgTransition()}

        ${mq.greaterThan('tablet')} {
          background-color: var(--color-img-placeholder);
          height: ${vw(380, 'desktop')};
          margin-bottom: ${vw(20, 'desktop')};
          max-width: 320px;
          max-height: 380px;
          width: ${vw(320, 'desktop')};
        }

        ${mq.greaterThan('desktop')} {
          margin-bottom: 20px;
        }
      }

      figcaption {
        ${getP18()}
        font-style: normal;
      }
    }
  }



`

const DivGroup = styled(motion.div)`
  align-items: flex-start;
  display: flex;
  flex-direction: column;
  gap: ${vw(10, 'mobile')};
  grid-column: 1 / span 6;
  justify-content: flex-start;

  ${mq.greaterThan('nexus7')} {
    gap: ${vw(10, 'tablet')};
  }

  ${mq.greaterThan('tablet')} {
    gap: ${vw(6, 'desktop')};
  }

  ${mq.greaterThan('desktop')} {
    gap: 6px;
  }

  &.g--root {
    z-index: 2;

    ${mq.greaterThan('tablet')} {
      grid-column: 1 / span 3;
      grid-row: 1 / span 1;
    }
  }

  &.g--sublevel {
    z-index: 2;

    + .g--root {
      ${mq.lessThan('tablet')} {
        display: none;
        opacity: 0!important;
      }
    }

    ${mq.greaterThan('tablet')} {
      grid-column: auto / span 3;
      grid-row: 1 / span 1;
      position: static;
    }

    ~ .g--sublevel {
      ${mq.lessThan('tablet')} {
        margin-top: 40px;
      }
    }
  }
`

const Dd = styled(motion.dd)`
  display: flex;

  &.first {
    margin-top: ${vw(60, 'mobile')};

    ${mq.greaterThan('nexus7')} {
      margin-top: ${vw(40, 'tablet')};
    }

    ${mq.greaterThan('tablet')} {
      margin-top: ${vw(40, 'desktop')};
    }

    ${mq.greaterThan('desktop')} {
      margin-top: 40px;
    }
  }

  button,
  a {
    display: inline-block;
    ${getP24_2()}
    opacity: .5;

    ${mq.greaterThan('tablet')} {
      letter-spacing: .27px;
    }
    &:hover,
    &.active {
      opacity: 1;
    }
  }

  .list--langs {
    align-items: flex-start;
    display: flex;
    flex-direction: row;
    gap: 10px;
    grid-row: 2 / span 1;
    justify-content: flex-start;

    ${mq.greaterThan('tablet')} {
      align-items: center;
      height: 100%;
      margin-right: auto;
      order: 2;
    }

    a {
      align-items: center;
      display: flex;
      justify-content: center;
      opacity: .7;
      padding: 5px;
      transition: 250ms opacity linear;

      &:hover,
      &.active {
        opacity: 1;
      }
    }
  }
`

const VARIANTS_LIST_GROUP = {
  animate: {
    transition: {
      staggerChildren: .05
    }
  }
}

const VARIANTS_ITEMS: Variants = {
  initial: {
    opacity: 0,
    x: '-20px',
    transition: {
      duration: .2,
      ease: 'easeOut'
    }
  },
  animate: {
    opacity: 1,
    x: '0',
    transition: {
      duration: .2,
      ease: 'easeOut'
    }
  },
  exit: {
    opacity: 0,
    x: '-20px',
    transition: {
      duration: .1,
      ease: 'easeOut'
    }
  }
}

const VARIANTS_ITEMS_TITLE: Variants = {
  initial: {
    opacity: 0,
    y: '-5px',
    transition: {
      duration: .2,
      ease: 'easeOut'
    }
  },
  animate: {
    opacity: .5,
    y: '0',
    transition: {
      duration: .2,
      ease: 'easeOut'
    }
  },
  exit: {
    opacity: 0,
    y: '-5px',
    transition: {
      duration: .1,
      ease: 'easeOut'
    }
  }
}
